<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import PurchaseIntegrationsAPI from 'dashboard/api/purchaseIntegrations';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();
const loading = ref(true);
const providers = ref([]);
const recentEvents = ref([]);
const saving = ref(null);
const forms = ref({
  asaas: { asaas_access_token: '', api_key: '' },
  hotmart: { hottok: '' },
});

const connectedCount = computed(
  () => providers.value.filter(p => p.connected).length
);

function humanDate(value) {
  return value ? new Date(value).toLocaleString() : '—';
}

function statusClass(provider) {
  if (provider.last_ingress_error) return 'text-red-600 dark:text-red-400';
  if (provider.connected) return 'text-green-700 dark:text-green-400';
  return 'text-n-slate-11';
}

async function load() {
  loading.value = true;
  try {
    const { data } = await PurchaseIntegrationsAPI.getStatus();
    providers.value = data.providers || [];
    recentEvents.value = data.recent_events || [];
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.PURCHASE.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
}

async function connect(provider) {
  saving.value = provider.id;
  try {
    const payload = {
      purchase_integration: {
        settings: forms.value[provider.id] || {},
      },
    };
    await PurchaseIntegrationsAPI.connect(provider.id, payload);
    useAlert(t('INTEGRATION_SETTINGS.PURCHASE.CONNECTED'));
    await load();
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.PURCHASE.SAVE_ERROR'));
  } finally {
    saving.value = null;
  }
}

async function disconnect(provider) {
  if (provider.id === 'shopify') return;
  saving.value = provider.id;
  try {
    await PurchaseIntegrationsAPI.disconnect(provider.id);
    useAlert(t('INTEGRATION_SETTINGS.PURCHASE.DISCONNECTED'));
    await load();
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.PURCHASE.SAVE_ERROR'));
  } finally {
    saving.value = null;
  }
}

async function copyUrl(url) {
  if (!url) return;
  await navigator.clipboard.writeText(url);
  useAlert(t('INTEGRATION_SETTINGS.PURCHASE.COPY_SUCCESS'));
}

onMounted(load);
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('INTEGRATION_SETTINGS.PURCHASE.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.PURCHASE.HEADER')"
        :description="$t('INTEGRATION_SETTINGS.PURCHASE.DESCRIPTION')"
        feature-name="integrations"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6 max-w-5xl">
        <p class="text-sm text-n-slate-11">
          {{ $t('INTEGRATION_SETTINGS.PURCHASE.SUMMARY', { count: connectedCount }) }}
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            v-for="provider in providers"
            :key="provider.id"
            class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 flex flex-col gap-3"
          >
            <div class="flex items-start justify-between gap-2">
              <div>
                <h3 class="text-base font-semibold text-n-slate-12">
                  {{ provider.label }}
                </h3>
                <p class="text-xs font-medium" :class="statusClass(provider)">
                  {{
                    provider.connected
                      ? $t('INTEGRATION_SETTINGS.PURCHASE.STATUS_CONNECTED')
                      : $t('INTEGRATION_SETTINGS.PURCHASE.STATUS_OFF')
                  }}
                </p>
              </div>
              <span
                v-if="provider.last_ingress_status"
                class="text-xs font-mono text-n-slate-11"
              >
                {{ provider.last_ingress_status }}
              </span>
            </div>

            <div v-if="provider.id === 'shopify'" class="text-sm text-n-slate-11">
              {{
                provider.shop_domain
                  ? $t('INTEGRATION_SETTINGS.PURCHASE.SHOPIFY_STORE', {
                      shop: provider.shop_domain,
                    })
                  : $t('INTEGRATION_SETTINGS.PURCHASE.SHOPIFY_HINT')
              }}
            </div>

            <div v-else-if="provider.webhook_url" class="flex flex-col gap-1">
              <span class="text-xs text-n-slate-11">{{
                $t('INTEGRATION_SETTINGS.PURCHASE.WEBHOOK_URL')
              }}</span>
              <code class="text-xs break-all bg-n-slate-3 rounded px-2 py-1">{{
                provider.webhook_url
              }}</code>
              <Button
                size="sm"
                variant="faded"
                class="self-start"
                @click="copyUrl(provider.webhook_url)"
              >
                {{ $t('INTEGRATION_SETTINGS.PURCHASE.COPY_URL') }}
              </Button>
            </div>

            <div
              v-if="provider.id === 'asaas' && !provider.connected"
              class="flex flex-col gap-2"
            >
              <Input
                v-model="forms.asaas.asaas_access_token"
                :label="$t('INTEGRATION_SETTINGS.PURCHASE.ASAAS_TOKEN')"
                type="password"
              />
              <Input
                v-model="forms.asaas.api_key"
                :label="$t('INTEGRATION_SETTINGS.PURCHASE.ASAAS_API_KEY')"
                type="password"
              />
            </div>

            <div
              v-if="provider.id === 'hotmart' && !provider.connected"
              class="flex flex-col gap-2"
            >
              <Input
                v-model="forms.hotmart.hottok"
                :label="$t('INTEGRATION_SETTINGS.PURCHASE.HOTMART_HOTTOK')"
                type="password"
              />
            </div>

            <p
              v-if="provider.last_ingress_error"
              class="text-xs text-red-600 dark:text-red-400"
            >
              {{ provider.last_ingress_error }}
            </p>

            <div class="flex gap-2 mt-auto">
              <Button
                v-if="provider.id !== 'shopify' && !provider.connected"
                size="sm"
                :is-loading="saving === provider.id"
                @click="connect(provider)"
              >
                {{ $t('INTEGRATION_SETTINGS.PURCHASE.ENABLE') }}
              </Button>
              <Button
                v-if="provider.id !== 'shopify' && provider.connected"
                size="sm"
                variant="faded"
                color="ruby"
                :is-loading="saving === provider.id"
                @click="disconnect(provider)"
              >
                {{ $t('INTEGRATION_SETTINGS.PURCHASE.DISABLE') }}
              </Button>
            </div>
          </div>
        </div>

        <section class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <h3 class="text-base font-semibold text-n-slate-12 mb-3">
            {{ $t('INTEGRATION_SETTINGS.PURCHASE.RECENT_EVENTS') }}
          </h3>
          <div v-if="recentEvents.length === 0" class="text-sm text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.PURCHASE.NO_EVENTS') }}
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full text-sm text-left">
              <thead class="text-xs text-n-slate-11 border-b border-n-weak">
                <tr>
                  <th class="py-2 pr-3">{{ $t('INTEGRATION_SETTINGS.PURCHASE.COL_TIME') }}</th>
                  <th class="py-2 pr-3">{{ $t('INTEGRATION_SETTINGS.PURCHASE.COL_SOURCE') }}</th>
                  <th class="py-2 pr-3">{{ $t('INTEGRATION_SETTINGS.PURCHASE.COL_TX') }}</th>
                  <th class="py-2 pr-3">{{ $t('INTEGRATION_SETTINGS.PURCHASE.COL_LAUNCH') }}</th>
                  <th class="py-2 pr-3">{{ $t('INTEGRATION_SETTINGS.PURCHASE.COL_STATUS') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="event in recentEvents"
                  :key="event.id"
                  class="border-b border-n-weak last:border-0"
                >
                  <td class="py-2 pr-3 whitespace-nowrap">{{ humanDate(event.received_at) }}</td>
                  <td class="py-2 pr-3">{{ event.source }}</td>
                  <td class="py-2 pr-3 font-mono text-xs">{{ event.transaction_id }}</td>
                  <td class="py-2 pr-3">{{ event.launch_id || '—' }}</td>
                  <td class="py-2 pr-3">
                    <span v-if="event.current_error" class="text-red-600">{{
                      event.current_error
                    }}</span>
                    <span v-else>{{ event.status }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
